import React, { useEffect, useRef, useState, memo } from "react";
import { Breadcrumb, Button, Spin, Tooltip } from "antd";
import { useRouter } from "next/router";
import Link from "next/link";
import styles from "@aspen/theme/Strategy.module.less";
import type { TKycLevelNumber, IVentureListItem } from "@aspen/model";
import { ISubAccountAuthType } from "@aspen/model";
import { redeemFund, subscribeFund } from "@aspen/services";
import {
  FUNDS_PATHS,
  GA_EVENT_NAME,
  GA_EVENT_TAG,
  hasPermissionOrNotSubAccount,
  i18nUtil,
  judgeProductStatus,
  reportEvent,
  safeParseJsonArrayWithReport,
} from "@aspen/libs";
import { useFetchFundDetail } from "@aspen/hooks";
import { WithKYCInfo, message } from "@aspen/ui";
import type {
  ISubscribeInfoRefModel,
  SubVentureResultInfoType,
  SellVentureResultInfoType,
} from "@aspen/widgets";
import {
  Disclaimer,
  ModalSubscribeSuccResult,
  ModalPrimarySubscribe,
  ModalFundRedeem,
  ModalSharesSuccResult,
} from "@aspen/widgets";
import Image from "next/image";
import logo from "@aspen/assets/images/aspen_logo_white.webp";
import { OpportunityOverview, TermsAndMetrics, ManagerBackground } from "@adgm/cross-ui";

interface IProps {
  showGuideModal: (level: TKycLevelNumber, isClose?: boolean) => boolean;
}
type ICompositeDetailType =
  | "fundTerms"
  | "performanceMetrics"
  | "opportunityOverview"
  | "managerBackground";

/** Fund 产品收益率 Table 组件 */
interface ProductDetailTableProps {
  performanceReturn?: string[][];
}

const ProductDetailTable: React.FC<ProductDetailTableProps> = memo(
  ({ performanceReturn }): React.ReactElement | null => {
    if (!performanceReturn || performanceReturn.length === 0) {
      return null;
    }

    return (
      <table className="w-full border-collapse">
        <tbody>
          {performanceReturn.map((row, rowIndex) => {
            let rowClassName = "h-[26px]";
            if (rowIndex === 0) {
              rowClassName = "bg-[var(--main-bg-color)] h-8";
            } else if (rowIndex % 2 === 0) {
              rowClassName = "bg-[var(--main-bg-color)] h-[26px]";
            }

            return (
              <tr key={rowIndex} className={rowClassName}>
                {row.map((col, colIdx) => (
                  <td
                    key={colIdx}
                    className="text-center text-[var(--white-op2)] border-r border-[var(--module-bg)] last:border-r-0 font-normal text-sm">
                    {col}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  },
);

const FundDetail: React.FC<IProps> = props => {
  const subResultInfoRef = useRef<SubVentureResultInfoType | null>(null);
  const redeemResultInfoRef = useRef<SellVentureResultInfoType | null>(null);
  const [modalSubResultVisible, setModalSubResultVisible] = useState<boolean>(false);
  const [primarySubscribe, setPrimarySubscribe] = useState<boolean>(false);
  const [modalRedeem, setModalRedeem] = useState<boolean>(false);
  const [modalRedeemResult, setmodalRedeemResult] = useState<boolean>(false);
  const intl = i18nUtil.t();
  const ventures_text: string = intl["column.fund"];
  const details_text: string = intl["ventures.detail.details"];

  const router = useRouter();
  const [info, updateInfo] = useState<IVentureListItem | null>(null);
  const parsedPurchaseInfo = safeParseJsonArrayWithReport(
    info?.purchaseInformation,
    `Error venture purchaseInformation: ${info?.id}`,
  );
  const attachment = safeParseJsonArrayWithReport(
    info?.attachment,
    `Error venture attachment: ${info?.id}`,
  );
  const compositeDetails = safeParseJsonArrayWithReport(
    info?.compositeDetails,
    `Error venture compositeDetails: ${info?.id}`,
  );
  const performanceReturnConfigList = info?.performanceReturnConfigList || [];
  const { data, isLoading } = useFetchFundDetail({ fundId: router.query?.id as string });

  useEffect(() => {
    if (data?.code == "0") {
      updateInfo(data?.data);
    } else if (data?.msg) {
      message.error(intl[data?.msg] ?? data?.msg);
    }
  }, [data?.code]);

  const handlePrimarySubmit = (subscribeInfo: ISubscribeInfoRefModel) => {
    if (!info?.id) return;
    const { amount } = subscribeInfo;
    subResultInfoRef.current = {
      assetType: info?.type,
      amount,
      currency: info?.currency,
      type: undefined,
    };
    const subscribeParams = {
      productId: info?.id,
      amount,
    };
    reportEvent({
      moduleName: GA_EVENT_NAME.fund.detail.submitSubscribe,
      detailParams: {
        ...subscribeParams,
      },
    });
    subscribeFund(subscribeParams)
      .then(res => {
        if (res?.code == "0") {
          reportEvent({
            joinedTag: GA_EVENT_TAG.Modal,
            moduleName: GA_EVENT_NAME.fund.detail.subsResult,
            detailParams: {
              ...subscribeParams,
            },
          });
          setModalSubResultVisible(true);
        } else {
          message.error(intl["venture.modal.subscribe.error.tips"]);
        }
      })
      .finally(() => {
        setPrimarySubscribe(false);
      });
  };
  // 点击申购赎回
  const handleModalRedeem = () => {
    if (props.showGuideModal(2)) {
      if (!info?.id) return;
      setModalRedeem(true);
      reportEvent({
        joinedTag: GA_EVENT_TAG.Modal,
        moduleName: GA_EVENT_NAME.fund.detail.redeemModal,
        detailParams: {
          id: info?.id,
        },
      });
    }
  };

  const handleRedeemSubmit = (listAmount: string) => {
    const params: any = {
      productId: info?.id ?? "",
      amount: listAmount,
    };
    redeemResultInfoRef.current = {
      isOther: true,
      projectName: info?.name ?? "",
      assetType: intl[`fund.capital.${info?.type ?? 0}`],
      amount: listAmount,
      shares: 0,
      currency: info?.currency,
    };
    reportEvent({
      moduleName: GA_EVENT_NAME.fund.detail.submitRedeem,
      detailParams: params,
    });
    redeemFund(params)
      .then(res => {
        if (res?.code == 0) {
          setmodalRedeemResult(true);
        } else {
          message.error(intl?.[res?.msg] ?? res?.msg);
        }
      })
      .catch(error => {
        message.error(intl?.[error?.msg] ?? error?.msg);
      })
      .finally(() => {
        setModalRedeem(false);
      });
  };

  if (!info) return null;

  const _renderDataroom = () => {
    return (
      <>
        {attachment.map((item, idx) => (
          <div
            key={idx}
            className={`px-6 py-4 text-white border-0 border-b border-solid border-[var(--border-color)] ${idx === attachment.length - 1 ? "border-b-0" : ""}`}>
            <a href={item.link} target="_blank" rel="noopener noreferrer">
              {item.name}
            </a>
          </div>
        ))}
      </>
    );
  };

  const getCurrentCompositeDetail = (type: ICompositeDetailType) => {
    return compositeDetails?.find(item => Object.keys(item)[0] === type)?.[type];
  };

  // 解析性能曲线图片URL
  const parsePerformanceCurveImageVolume = (imageUrlString: string) => {
    const imageUrlArray = safeParseJsonArrayWithReport(
      imageUrlString,
      "Failed to parse performance curve image URLs",
    );
    return imageUrlArray.find(item => item.web)?.web || null;
  };

  // 解析copyWriter信息
  const parseCopyWriter = (copyWriterString: string) => {
    const copyWriterArray = safeParseJsonArrayWithReport(
      copyWriterString,
      "Failed to parse copyWriter data",
    );
    const performanceFee = copyWriterArray.find(item => item.performanceFee)?.performanceFee || "";
    const indicatesEstimatedPerformance =
      copyWriterArray.find(item => item.indicatesEstimatedPerformance)
        ?.indicatesEstimatedPerformance || "";
    return { performanceFee, indicatesEstimatedPerformance };
  };

  return (
    <div className={styles.detail}>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link
            href={{
              pathname: FUNDS_PATHS.FUNDS,
            }}>
            {ventures_text}
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{details_text}</Breadcrumb.Item>
      </Breadcrumb>
      <section id="title">
        <div className="flex justify-between items-center">
          <p className={styles.title}>{info.name}</p>
          {attachment?.length ? (
            <Tooltip
              placement="bottomRight"
              trigger="click"
              color="black"
              title={_renderDataroom()}>
              <Button type="primary">{intl["fund.detail.dataroom"]}</Button>
            </Tooltip>
          ) : null}
        </div>
      </section>
      <Spin spinning={isLoading}>
        <div className="w-full relative bg-[var(--module-bg)] p-8">
          <div className="flex justify-between">
            <div className="relative">
              <Image
                className="absolute top-[30px] left-[30px]"
                unoptimized
                width="112"
                height="48"
                src={logo}
                alt="logo"
              />
              <Image
                className="rounded-2xl object-cover"
                unoptimized
                width="556"
                height="382"
                src={info.listPictureOnSaleAddressWeb}
                alt=""
              />
            </div>
            <div className="ml-8 flex-1 h-[382px] bg-[var(--fund-dashboard-bg)] rounded-2xl p-6 flex flex-col gap-8">
              <p className="w-full text-[28px] font-bold text-white leading-[42px]">{info.name}</p>
              <div className="w-full h-[140px] px-8 py-4 bg-[var(--fund-dashboard-detail-bg)] rounded-lg flex flex-wrap justify-between text-center border border-[var(--fund-dashboard-detail-border)]">
                {parsedPurchaseInfo?.length > 0
                  ? parsedPurchaseInfo?.map((item, index) => (
                      <div className="flex-none basis-1/2 flex flex-col justify-center" key={index}>
                        <p className="text-[12px] text-[var(--text-color)] leading-[20px]">
                          {item?.title || "--"}
                        </p>
                        <p className="text-[14px] font-medium text-white ">
                          {item?.subTitle || "--"}
                        </p>
                      </div>
                    ))
                  : null}
              </div>
              {hasPermissionOrNotSubAccount(ISubAccountAuthType.FUNDS) ? (
                <div className="w-full">
                  {judgeProductStatus(Number(info.startTime), Number(info.endTime)) ===
                  "ongoing" ? (
                    <div className="flex justify-between gap-8">
                      <Button
                        type="primary"
                        className="w-full"
                        size="large"
                        onClick={() => {
                          reportEvent({
                            joinedTag: GA_EVENT_TAG.Modal,
                            moduleName: GA_EVENT_NAME.fund.detail.submitSubscribe,
                            detailParams: {
                              id: info.id,
                            },
                          });
                          setPrimarySubscribe(true);
                        }}>
                        {intl["button.subscribe"]}
                      </Button>
                      {info.amount ? (
                        <Button
                          type="default"
                          disabled={!info.amount}
                          size="large"
                          className="w-full"
                          onClick={handleModalRedeem}>
                          {intl["button.redeem"]}
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="w-full">
                      <Button type="primary" disabled size="large" className="w-full">
                        {judgeProductStatus(Number(info.startTime), Number(info.endTime)) ===
                        "close"
                          ? intl["button.closed"]
                          : intl["button.comingSoon"]}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
          <div className="h-[1px] bg-[var(--fund-dashboard-detail-border)] my-8" />
          {compositeDetails?.length ? (
            <div className="flex flex-wrap mb-8 !items-stretch border-t border-[var(--fund-dashboard-detail-border)] gap-8">
              <div className="flex flex-col flex-none basis-[calc(50%-16px)]">
                <OpportunityOverview
                  text={getCurrentCompositeDetail("opportunityOverview")?.text}
                  title={getCurrentCompositeDetail("opportunityOverview")?.title}
                />
              </div>
              <div className="flex flex-col flex-none basis-[calc(50%-16px)]">
                <TermsAndMetrics
                  detail={getCurrentCompositeDetail("fundTerms")?.detail}
                  title={getCurrentCompositeDetail("fundTerms")?.title}
                />
              </div>
              <div className="flex flex-col flex-none basis-[calc(50%-16px)]">
                <ManagerBackground
                  text={getCurrentCompositeDetail("managerBackground")?.text}
                  title={getCurrentCompositeDetail("managerBackground")?.title}
                  image={getCurrentCompositeDetail("managerBackground")?.image}
                />
              </div>
              <div className="flex flex-col flex-none basis-[calc(50%-16px)]">
                <TermsAndMetrics
                  detail={getCurrentCompositeDetail("performanceMetrics")?.detail}
                  title={getCurrentCompositeDetail("performanceMetrics")?.title}
                />
              </div>
            </div>
          ) : null}
          {/* 渲染性能回报配置列表 */}
          {performanceReturnConfigList?.length > 0 &&
            performanceReturnConfigList.map((config, index) => {
              const curveImageUrl = parsePerformanceCurveImageVolume(
                config.performanceCurveImageUrl,
              );
              const copyWriter = parseCopyWriter(config.copyWriter);
              const performanceReturn = safeParseJsonArrayWithReport(config.performanceReturn);
              return (
                <div key={config.id || index} className="mb-8">
                  {/* 配置标题 */}
                  <p className="text-[22px] font-bold text-[var(--white-op3)] mb-6">
                    {config.title || "--"}
                  </p>
                  {/* 性能曲线图片 */}
                  {curveImageUrl && (
                    <Image
                      className={`rounded-2xl h-auto ${performanceReturn?.length > 0 ? "mb-8" : ""}`}
                      width={1144}
                      height={811}
                      unoptimized
                      src={curveImageUrl}
                      alt={config.title || "Performance Curve"}
                    />
                  )}
                  {performanceReturn?.length > 0 ? (
                    <>
                      <p className="text-[22px] font-bold text-[var(--white-op3)] mb-6">
                        {intl["fund.detail.return.performance"]}
                      </p>
                      {/* 性能回报表格 */}
                      <ProductDetailTable performanceReturn={performanceReturn} />
                    </>
                  ) : null}
                  {/* copyWriter 信息 */}
                  {(copyWriter.performanceFee || copyWriter.indicatesEstimatedPerformance) && (
                    <div className="mt-[10px] text-[12px] text-[var(--white-op2)]">
                      {copyWriter.performanceFee && <div>{copyWriter.performanceFee}</div>}
                      {copyWriter.indicatesEstimatedPerformance && (
                        <div className="text-right">{copyWriter.indicatesEstimatedPerformance}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        {info.name ? <Disclaimer /> : null}
      </Spin>
      {primarySubscribe ? (
        <ModalPrimarySubscribe
          productType="fund"
          visible={primarySubscribe}
          info={info}
          purchaseInformation={info.purchaseInformation || ""}
          handleSubmit={handlePrimarySubmit}
          cancelModal={() => {
            setPrimarySubscribe(false);
          }}
        />
      ) : null}
      {modalSubResultVisible ? (
        <ModalSubscribeSuccResult
          productType="fund"
          visible={modalSubResultVisible}
          ventureName={info.name}
          subResultInfo={subResultInfoRef?.current}
          cancelModal={() => {
            setModalSubResultVisible(false);
          }}
        />
      ) : null}
      {modalRedeem ? (
        <ModalFundRedeem
          visible={modalRedeem}
          cancelModal={() => {
            setModalRedeem(false);
          }}
          handleSubmit={handleRedeemSubmit}
          selectFund={info}
        />
      ) : null}
      {modalRedeemResult ? (
        <ModalSharesSuccResult
          productType="fund"
          visible={modalRedeemResult}
          sellResultInfo={redeemResultInfoRef.current}
          cancelModal={() => {
            setmodalRedeemResult(false);
          }}
        />
      ) : null}
    </div>
  );
};

export const PageFundDetail = WithKYCInfo(FundDetail);
